import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { paymentsApi } from '../../api/payments'
import { couponsApi } from '../../api/coupons'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import { FiCheck, FiX, FiTag } from 'react-icons/fi'
import './Payments.css'

const Payments = () => {
  const [searchParams] = useSearchParams()
  const courseId = searchParams.get('course_id')
  const packageId = searchParams.get('package_id')

  const parseSubjectIds = () => {
    const values = [
      ...searchParams.getAll('subject_ids'),
      ...searchParams.getAll('selected_subject_ids'),
      ...searchParams.getAll('selected_subject_ids[]'),
    ]

    const ids = values
      .flatMap((value) => String(value || '').split(','))
      .map((id) => parseInt(String(id).trim(), 10))
      .filter((id) => Number.isInteger(id) && id > 0)

    return Array.from(new Set(ids))
  }

  const selectedSubjectIds = parseSubjectIds()
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [showCouponField, setShowCouponField] = useState(false)
  const [appliedCoupon, setAppliedCoupon] = useState(null)
  const [couponError, setCouponError] = useState(null)

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      payment_method: 'instapay',
      transfer_datetime: new Date().toISOString().slice(0, 16),
    }
  })

  const paymentMethod = watch('payment_method')

  const validateCouponMutation = useMutation({
    mutationFn: (code) => couponsApi.validateCoupon({
      code: code.toUpperCase(),
      course_id: courseId ? parseInt(courseId) : null,
      package_id: packageId ? parseInt(packageId) : null,
    }),
    onSuccess: (data) => {
      setAppliedCoupon(couponCode.toUpperCase())
      setCouponError(null)
      setCouponCode('')
    },
    onError: (error) => {
      setCouponError(error.response?.data?.message || 'كود الخصم غير صحيح')
      setAppliedCoupon(null)
    },
  })

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      setCouponError('يرجى إدخال كود الخصم')
      return
    }
    validateCouponMutation.mutate(couponCode.trim())
  }

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null)
    setCouponCode('')
    setCouponError(null)
  }

  const onSubmit = async (data) => {
    if (!courseId && !packageId) {
      setError('يرجى تحديد دورة أو باقة للدفع')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const formData = new FormData()
      
      // Ensure IDs are integers
      if (courseId) {
        formData.append('course_id', parseInt(courseId))
      }
      if (packageId) {
        formData.append('package_id', parseInt(packageId))
      }
      
      formData.append('payment_method', data.payment_method)
      formData.append('transfer_datetime', data.transfer_datetime)
      
      if (data.payment_method === 'instapay') {
        formData.append('instapay_number', data.instapay_number)
      } else if (data.payment_method === 'wallet') {
        formData.append('wallet_number', data.wallet_number)
      }
      
      if (data.transaction_photo && data.transaction_photo[0]) {
        formData.append('transaction_photo', data.transaction_photo[0])
      }

      // Preserve selected subjects for subject-selection packages
      if (packageId && selectedSubjectIds.length > 0) {
        selectedSubjectIds.forEach((subjectId) => {
          formData.append('selected_subject_ids[]', subjectId)
        })
      }

      // Debug: Log what we're sending
      console.log('Submitting payment:', {
        course_id: courseId,
        package_id: packageId,
        payment_method: data.payment_method,
        selected_subject_ids: selectedSubjectIds,
      })

      const response = await paymentsApi.createPayment(formData)
      
      if (response.success) {
        setSuccess(true)
        setError(null)
        // Optionally redirect after a delay
        setTimeout(() => {
          window.location.href = '/my-courses'
        }, 3000)
      } else {
        setError(response.message || 'حدث خطأ أثناء إنشاء عملية الدفع')
      }
    } catch (error) {
      console.error('Payment error:', error)
      const errorData = error.response?.data
      let errorMessage = 'حدث خطأ أثناء إنشاء عملية الدفع'
      
      if (errorData?.errors) {
        // Laravel validation errors
        const errorMessages = Object.values(errorData.errors).flat()
        errorMessage = errorMessages.join('\n')
      } else if (errorData?.message) {
        errorMessage = errorData.message
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="payments-page bg-gray-900 min-h-screen">
      <Header />
      <main className="payments-main bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="payment-card">
            <h1 className="page-title text-white">إرسال إيصال الدفع</h1>
            <p className="payment-description">
              يرجى ملء النموذج أدناه وإرفاق صورة إيصال التحويل
            </p>

            {/* Coupon Section */}
            <div className="coupon-section">
              <div className="coupon-header">
                <div className="coupon-header-content">
                  <FiTag className="coupon-icon" />
                  <span>هل لديك كود خصم؟</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCouponField(!showCouponField)}
                >
                  {showCouponField ? 'إخفاء' : 'تطبيق'}
                </Button>
              </div>
              
              {showCouponField && (
                <div className="coupon-input-section">
                  <div className="coupon-input-wrapper">
                    <input
                      type="text"
                      className="coupon-input"
                      placeholder="أدخل كود الخصم"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value)
                        setCouponError(null)
                      }}
                      disabled={!!appliedCoupon}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !appliedCoupon) {
                          handleApplyCoupon()
                        }
                      }}
                    />
                    {!appliedCoupon && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleApplyCoupon}
                        loading={validateCouponMutation.isLoading}
                      >
                        تطبيق
                      </Button>
                    )}
                  </div>
                  
                  {couponError && (
                    <div className="coupon-error">
                      <p>{couponError}</p>
                    </div>
                  )}
                  
                  {appliedCoupon && (
                    <div className="coupon-success">
                      <div className="coupon-success-content">
                        <FiCheck className="success-icon" />
                        <span>تم تطبيق كود الخصم "{appliedCoupon}"</span>
                      </div>
                      <button
                        type="button"
                        className="remove-coupon-btn"
                        onClick={handleRemoveCoupon}
                      >
                        <FiX />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {success && (
              <div className="success-message">
                <p>✓ تم إرسال طلب الدفع بنجاح. سيتم مراجعته من قبل الإدارة.</p>
                <p>سيتم توجيهك إلى صفحة دوراتي...</p>
              </div>
            )}

            {error && (
              <div className="error-message">
                <p>{error}</p>
              </div>
            )}

            {!success && (
              <form onSubmit={handleSubmit(onSubmit)} className="payment-form">
                <div className="form-group">
                  <label htmlFor="payment_method">طريقة الدفع *</label>
                  <select
                    id="payment_method"
                    {...register('payment_method', { required: 'يرجى اختيار طريقة الدفع' })}
                    className={errors.payment_method ? 'error' : ''}
                  >
                    <option value="instapay">إنستاباي</option>
                    <option value="wallet">محفظة</option>
                  </select>
                  {errors.payment_method && (
                    <span className="field-error">{errors.payment_method.message}</span>
                  )}
                </div>

                {paymentMethod === 'instapay' && (
                  <div className="form-group">
                    <label htmlFor="instapay_number">رقم إنستاباي *</label>
                    <Input
                      id="instapay_number"
                      type="text"
                      {...register('instapay_number', {
                        required: paymentMethod === 'instapay' ? 'يرجى إدخال رقم إنستاباي' : false,
                      })}
                      error={errors.instapay_number?.message}
                    />
                  </div>
                )}

                {paymentMethod === 'wallet' && (
                  <div className="form-group">
                    <label htmlFor="wallet_number">رقم المحفظة *</label>
                    <Input
                      id="wallet_number"
                      type="text"
                      {...register('wallet_number', {
                        required: paymentMethod === 'wallet' ? 'يرجى إدخال رقم المحفظة' : false,
                      })}
                      error={errors.wallet_number?.message}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="transfer_datetime">تاريخ ووقت التحويل *</label>
                  <Input
                    id="transfer_datetime"
                    type="datetime-local"
                    {...register('transfer_datetime', {
                      required: 'يرجى إدخال تاريخ ووقت التحويل',
                    })}
                    error={errors.transfer_datetime?.message}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="transaction_photo">صورة إيصال التحويل *</label>
                  <input
                    id="transaction_photo"
                    type="file"
                    accept="image/*"
                    {...register('transaction_photo', {
                      required: 'يرجى إرفاق صورة إيصال التحويل',
                    })}
                    className={errors.transaction_photo ? 'error' : ''}
                  />
                  {errors.transaction_photo && (
                    <span className="field-error">{errors.transaction_photo.message}</span>
                  )}
                  <small className="form-hint">الحجم الأقصى: 5 ميجابايت</small>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={loading}
                >
                  إرسال طلب الدفع
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Payments

